import {
  type BrowserSessionStatus,
  type Channel,
  strategyKeysFor,
} from '@sbc/domain-models/gateway';
import { Link } from '@tanstack/react-router';
import { MonitorPlay, Pencil, Plus } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useChannelConnection } from '@/lib/api/channel-connections';
import { BindingRowCells } from '@/routes/gateway/binding-row';
import { ConnectionAccountFields } from '@/routes/gateway/connection-account-fields';
import { IntercomHistoryImportPanel } from '@/routes/gateway/intercom-history-import-panel';
import { STRATEGIES } from '@/routes/gateway/strategies';

const SESSION_LABELS: Record<BrowserSessionStatus | 'unknown', string> = {
  ok: 'Bot logged in',
  needs_login: 'Bot needs login',
  unknown: 'Bot not checked yet',
};

// Explains the strategies this channel can run and how one inbound event maps to exactly one of
// them, so an operator adding bindings knows which to enable and what each will do. Mirrors the
// gateway routing: on Instagram, DMs → reply to DM and a comment is routed by which connected
// account it tags (the post's owner → fan out, another connected account → that account's reply
// to mention), with comments tagging no answerable account dropped.
function StrategyGuide({ channel }: { channel: Channel }) {
  return (
    <div className="mb-4 space-y-3 rounded-md border bg-[var(--color-muted)]/40 p-4 text-sm">
      <p className="text-[var(--color-muted-foreground)]">
        Each inbound event is handled by exactly one strategy. Add a binding for each strategy this
        account should run — a strategy with no enabled binding is skipped, which is also the kill
        switch: disabling a binding stops new runs.
      </p>
      <dl className="space-y-2">
        {strategyKeysFor(channel).map((key) => (
          <div key={key} className="grid gap-1 sm:grid-cols-[10rem_1fr] sm:gap-3">
            <dt>
              <Badge variant="secondary">{STRATEGIES[key].label}</Badge>
            </dt>
            <dd className="text-[var(--color-muted-foreground)]">
              <span className="text-[var(--color-foreground)]">{STRATEGIES[key].applies}</span>{' '}
              {STRATEGIES[key].help}
            </dd>
          </div>
        ))}
      </dl>
      {channel === 'instagram' && (
        <p className="text-[var(--color-muted-foreground)]">
          Comments arrive on the webhook of the account that owns the post, so the gateway routes by
          which connected account the comment tags: the post owner's own handle →{' '}
          <span className="font-medium">Fan out on comment</span>; a comment on another connected
          account's post that tags this handle →{' '}
          <span className="font-medium">Reply to mention</span>, answered publicly as this account.
          A comment that tags no connected account with a matching binding is dropped, and a comment
          written by one of our own accounts never routes.
        </p>
      )}
    </div>
  );
}

export function ConnectionDetailPage({ connectionId }: { connectionId: string }) {
  const connectionQuery = useChannelConnection(connectionId);

  if (connectionQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (connectionQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load connection: {connectionQuery.error.message}
      </p>
    );
  }

  const connection = connectionQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{connection.handle}</h1>
          <p className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            <Badge variant="secondary">{connection.channel}</Badge>
            {connection.enabled ? (
              <Badge variant="secondary">Enabled</Badge>
            ) : (
              <Badge variant="outline">Disabled</Badge>
            )}
            {connection.channel === 'facebook_group' && (
              <Badge variant={connection.sessionStatus === 'ok' ? 'secondary' : 'outline'}>
                {SESSION_LABELS[connection.sessionStatus ?? 'unknown']}
              </Badge>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {connection.channel === 'facebook_group' && (
            <Button asChild variant="outline">
              <Link to="/gateway/connections/$connectionId/live" params={{ connectionId }}>
                <MonitorPlay className="size-4" />
                Live view
              </Link>
            </Button>
          )}
          <Button asChild variant="outline">
            <Link to="/gateway/connections/$connectionId/edit" params={{ connectionId }}>
              <Pencil className="size-4" />
              Edit
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/gateway/connections">Back</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ConnectionAccountFields connection={connection} />
          {connection.channel === 'slack' && (
            <div className="space-y-1">
              <p className="text-sm font-medium">Connected teammates</p>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                {connection.slackUserGrantCount} — people whose translations are written under their
                own messages.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {connection.channel === 'intercom' && <IntercomHistoryImportPanel />}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Bindings</CardTitle>
          <Button asChild size="sm">
            <Link to="/gateway/bindings/new" search={{ connectionId }}>
              <Plus className="size-4" />
              Add binding
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          <StrategyGuide channel={connection.channel} />
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Strategy</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {connection.bindings.length ? (
                  connection.bindings.map((binding) => (
                    <TableRow key={binding.id}>
                      <BindingRowCells binding={binding} />
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-20 text-center">
                      No bindings yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
